// Transport for a VISITOR adding photos to a shared logbook trip:
// shareLogbookTrip.mjs's sibling, on the same terms.
//
// THREE REQUESTS, not one. A Function URL request body caps at 6 MB and a phone
// photo is 4-6 MB before base64 inflates it by a third, so the bytes cannot go
// through the Lambda. Instead:
//
//   1. POST 'presign'  -> the endpoint validates the share, the day and every
//                         file, and hands back one presigned PUT per photo.
//   2. PUT each photo   -> straight to S3. The attachments bucket's CORS is
//                         Amplify's default (allowedOrigins ['*'], PUT among the
//                         methods), so this works with no storage change.
//   3. POST 'complete'  -> the endpoint HeadObjects what we claim we uploaded,
//                         mails the owner, and hands back presigned GETs for the
//                         new photos so the page can splice them in rather than
//                         refetching the whole share.
//
// NOT StorageManager, NOT Amplify uploadData. An anonymous visitor has no Amplify
// Storage credentials at all -- which is the same reason the read path presigns
// its GETs. SharedLogbookTrip.test.jsx mocks aws-amplify/storage with a
// mockAmplify that REJECTS any operation it was not given, so "this route touches
// no Amplify Storage" stays a real assertion, and this file must not be what
// breaks it.
//
// shareUploadAvailable() is the deploy gate, and it guards MORE than the button:
// the one backend deploy carries the Function URL and the share row's
// allowUploads / notifyEmail columns together, so the URL's presence in
// amplify_outputs.json IS "the schema has those columns" -- and AppSync rejects a
// WHOLE query naming a field the branch does not have. Every owner-side call that
// names either column checks this first.

import outputs from '../amplify_outputs.json';
import {
  guestUploadRejection,
  MAX_GUEST_UPLOAD_FILES,
} from '../fleet-shared/Attachments.mjs';

function shareUploadBase() {
  return outputs?.custom?.shareUploadFunctionUrl || '';
}

export function shareUploadAvailable() {
  return Boolean(shareUploadBase());
}

async function postAction(payload) {
  const base = shareUploadBase();
  if (!base) throw new Error('Adding photos is not available.');

  const response = await fetch(base, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  // The body is worth reading even on a failure: the endpoint answers 403 with
  // "this trip is not accepting photos" and 429 with a cap message, and both are
  // things a visitor should be told rather than "something went wrong".
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    const error = new Error(body?.error || `Adding photos failed (${response.status}).`);
    error.status = response.status;
    // 410 means the link has been revoked (or the trip is gone) while this page
    // was open -- distinguishable so the page can say so rather than blaming the
    // photos.
    error.gone = response.status === 404 || response.status === 410;
    error.rejected = Array.isArray(body?.rejected) ? body.rejected : [];
    throw error;
  }

  return body ?? {};
}

// Reshaped rather than passed through, the read transport's rule: a key the
// Lambda adds and this omits simply never reaches the page.
export async function requestUploadSlots({ shareId, entryId, files }) {
  const body = await postAction({
    action: 'presign',
    shareId,
    entryId,
    files: files.map((file) => ({ name: file.name, size: file.size })),
    // The honeypot field the endpoint checks, always empty from a real client.
    company: '',
  });

  return {
    uploads: Array.isArray(body.uploads) ? body.uploads : [],
    rejected: Array.isArray(body.rejected) ? body.rejected : [],
  };
}

// One photo, straight to S3.
//
// THE CONTENT TYPE MUST BE THE ONE THE SERVER SIGNED, exactly. It is derived from
// the file's extension server-side and signed into the URL, so sending
// `file.type` instead -- which a browser may spell differently, or leave empty --
// is a signature mismatch that arrives as a bare 403 and reads like an expired
// URL. Send back what we were given.
export async function putPhoto(upload, file) {
  const response = await fetch(upload.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': upload.contentType },
  });
  if (!response.ok) {
    throw new Error(`Could not upload ${file.name} (${response.status}).`);
  }
  return upload.key;
}

export async function completeUpload({ shareId, entryId, keys, uploaderName }) {
  const body = await postAction({
    action: 'complete',
    shareId,
    entryId,
    keys,
    uploaderName: uploaderName || '',
  });

  return {
    stored: Number(body.stored) || 0,
    notified: body.notified === true,
    photos: Array.isArray(body.photos) ? body.photos : [],
  };
}

/**
 * The whole flow, with per-file progress.
 *
 * Resolves `{ photos, notified, rejected, failed }`:
 *   photos   - presigned, ready to splice into the page
 *   rejected - files the endpoint would not sign, each with a reason
 *   failed   - files whose PUT itself failed, each with a reason
 *
 * A failed PUT does NOT abort the batch. Someone adding eight photos on marina
 * wifi should not lose seven of them to one timeout, and `complete` is told only
 * about the keys that actually landed.
 */
export async function uploadSharedPhotos({ shareId, entryId, files, uploaderName, onProgress }) {
  if (!shareUploadAvailable()) throw new Error('Adding photos is not available.');

  const picked = Array.from(files ?? []);
  if (picked.length === 0) throw new Error('No photos were chosen.');

  // Pre-checked against the SAME rule the endpoint enforces, so an obviously-bad
  // file is named without a round trip. The endpoint checks again -- the doubled
  // whitelist, the same shape as the field whitelist on the read side.
  const rejected = [];
  const sendable = [];
  for (const file of picked) {
    const reason = guestUploadRejection(file);
    if (reason) rejected.push({ name: file.name, reason });
    else if (sendable.length < MAX_GUEST_UPLOAD_FILES) sendable.push(file);
    else rejected.push({ name: file.name, reason: `Only ${MAX_GUEST_UPLOAD_FILES} at a time.` });
  }

  if (sendable.length === 0) {
    return { photos: [], notified: false, rejected, failed: [] };
  }

  const { uploads, rejected: refused } = await requestUploadSlots({
    shareId,
    entryId,
    files: sendable,
  });
  rejected.push(...refused);

  const keys = [];
  const failed = [];
  let done = 0;

  // Sequential, for photoZip.mjs's reason from the other direction: several
  // multi-megabyte PUTs at once on a phone's uplink is slower than one at a time
  // and far more likely to time out.
  for (const upload of uploads) {
    // BY INDEX, NOT BY NAME. A phone multi-select routinely yields two files
    // called `IMG_0421.jpg` from different albums, and a Map keyed on the name
    // collapses them: one File would be PUT to BOTH signed keys while the other
    // vanished, and the visitor would be told both had been added. The endpoint
    // echoes the position of the file it signed for exactly this.
    //
    // Its own index, not a positional zip: `uploads` is a subsequence of what we
    // sent, because a rejected file is skipped.
    //
    // No fallback for an `index`-less response, deliberately: this endpoint has
    // never been deployed without it, so there is no older handler to be skewed
    // against -- and every fallback available here is the name matching this
    // replaced.
    const file = sendable[upload.index];
    if (!file) continue;
    try {
      keys.push(await putPhoto(upload, file));
    } catch (err) {
      failed.push({ name: upload.name, reason: err.message });
    }
    done += 1;
    if (onProgress) onProgress({ done, total: uploads.length });
  }

  if (keys.length === 0) {
    return { photos: [], notified: false, rejected, failed };
  }

  const { photos, notified } = await completeUpload({ shareId, entryId, keys, uploaderName });
  return { photos, notified, rejected, failed };
}
